/**
 * subscription.js
 * Happy Hounds — Subscriptions page JS
 *
 * Handles:
 *  1. Tab switching (with URL hash sync)
 *  2. Cancel popup (open / close)
 *  3. FAQ accordion
 *  4. sendEmail() helper
 *
 * No jQuery. No external dependencies.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     1. TAB SYSTEM
     Tab buttons live in subscription-hero.liquid and dispatch
     a 'hhTabChange' CustomEvent on document.
     Content sections carry data-sub-tab="new|manage|faq"
     on their .sub-section inner wrapper.
     This listener shows/hides the parent .shopify-section.
  ───────────────────────────────────────── */

  var DEFAULT_TAB = 'new';

  /* In the Shopify theme editor (design mode) sections are NEVER hidden —
     all tab groups render stacked so merchants can always see and edit any
     section. Hiding only happens on the real storefront. Without this,
     sections in non-active tabs look like they've vanished in the editor
     (and re-appear/disappear unpredictably around saves and re-renders). */
  var IN_EDITOR = !!(window.Shopify && window.Shopify.designMode);

  /* Track the active tab so it survives editor section re-renders */
  var currentTab = DEFAULT_TAB;

  function activateTab(tabId) {
    if (!tabId) tabId = DEFAULT_TAB;
    currentTab = tabId;

    /* Show/hide sections — storefront only; in the editor everything stays visible */
    document.querySelectorAll('.sub-section[data-sub-tab]').forEach(function (el) {
      var parent = el.closest('.shopify-section') || el;
      var match = el.dataset.subTab === tabId || el.dataset.subTab === 'always';
      parent.style.display = (match || IN_EDITOR) ? '' : 'none';
    });

    /* Sync tab button active state */
    document.querySelectorAll('.sub-tab-btn').forEach(function (btn) {
      var active = btn.dataset.subTabTarget === tabId;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    /* Update URL hash without scrolling */
    try {
      history.replaceState(null, '', '#tab=' + tabId);
    } catch (e) { /* noop in sandboxed iframes */ }
  }

  function readTabFromHash() {
    var hash = window.location.hash;
    var match = hash.match(/[#&]tab=([^&]+)/);
    return match ? match[1] : null;
  }

  document.addEventListener('hhTabChange', function (e) {
    activateTab(e.detail && e.detail.tab);
  });

  /* On DOMContentLoaded, activate the correct tab */
  document.addEventListener('DOMContentLoaded', function () {
    activateTab(readTabFromHash() || DEFAULT_TAB);
  });

  /* Handle browser back/forward */
  window.addEventListener('hashchange', function () {
    activateTab(readTabFromHash() || DEFAULT_TAB);
  });

  /* ─────────────────────────────────────────
     THEME EDITOR SUPPORT (Shopify design mode)
     All sections stay visible in the editor (see IN_EDITOR above), so
     nothing can "go missing" while editing. These listeners just keep the
     tab buttons' active state in sync and scroll to a section when it's
     selected in the sidebar.
  ───────────────────────────────────────── */
  if (IN_EDITOR) {
    /* Editor re-rendered a section (settings change, hide/unhide, reorder) —
       re-apply state (a no-op for visibility in editor, syncs tab buttons) */
    document.addEventListener('shopify:section:load', function () {
      activateTab(currentTab);
    });

    /* Merchant clicked a section in the editor sidebar — mark its tab active
       and bring the section into view */
    document.addEventListener('shopify:section:select', function (e) {
      var el = e.target && e.target.querySelector('.sub-section[data-sub-tab]');
      if (!el) return;
      var tab = el.dataset.subTab;
      if (tab && tab !== 'always' && tab !== currentTab) activateTab(tab);
    });
  }

  /* ─────────────────────────────────────────
     2. CANCEL POPUP
     Popup data is stored in window.HHCancelReasons (set by
     subscription-cancel.liquid) as an array of objects:
     {
       key: 'manage',
       eyebrow: '...',
       title: '...',
       intro: '...',
       solutions: [{ icon, title, body }, ...]
       ctaLabel: '...',
       ctaUrl: '...'
     }
  ───────────────────────────────────────── */

  window.HHCancelReasons = window.HHCancelReasons || [];

  function buildPopupHTML(reason) {
    var items = (reason.solutions || []).map(function (s) {
      /* s.icon is a CDN URL (from image_picker) or null — render as <img> when present */
      var iconHTML = s.icon
        ? '<div class="sub-solution-icon"><img src="' + s.icon + '" class="sub-icon-img" width="24" height="24" alt="" /></div>'
        : '';
      return '<div class="sub-solution-item">'
        + iconHTML
        + '<div>'
        + '<div class="sub-solution-title">' + (s.title || '') + '</div>'
        + '<div class="sub-solution-body">' + (s.body || '') + '</div>'
        + '</div>'
        + '</div>';
    }).join('');

    return '<button class="sub-popup-close" onclick="HHPopup.close()" aria-label="Close">✕</button>'
      + '<div class="sub-popup-eyebrow">' + (reason.eyebrow || '') + '</div>'
      + '<div class="sub-popup-title">' + (reason.title || '') + '</div>'
      + '<div class="sub-popup-intro">' + (reason.intro || '') + '</div>'
      + items
      + '<a href="' + (reason.ctaUrl || '/account') + '" class="sub-popup-cta">' + (reason.ctaLabel || 'Go to my dashboard') + ' →</a>'
      + '<button class="sub-popup-dismiss" onclick="HHPopup.close()">I still want to cancel</button>';
  }

  /* The overlay is rendered inside the cancel section, but position:fixed
     breaks inside any ancestor with a CSS transform/filter (the theme's
     section scroll-in animations add transforms). Re-parent it to <body>
     so it always covers the viewport. */
  function liftOverlayToBody() {
    var overlay = document.getElementById('sub-popup-overlay');
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  document.addEventListener('DOMContentLoaded', liftOverlayToBody);

  window.HHPopup = {
    open: function (key) {
      /* Lift again at open time — covers the theme editor re-rendering the
         section (which re-creates the overlay inside the section) */
      var overlay = liftOverlayToBody();
      var content = document.getElementById('sub-popup-content');
      if (!overlay || !content) return;

      var reason = (window.HHCancelReasons || []).find(function (r) { return r.key === key; });
      if (!reason) return;

      content.innerHTML = buildPopupHTML(reason);
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },
    close: function () {
      var overlay = document.getElementById('sub-popup-overlay');
      if (overlay) overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  /* Close on overlay backdrop click */
  document.addEventListener('click', function (e) {
    if (e.target && e.target.id === 'sub-popup-overlay') {
      HHPopup.close();
    }
  });

  /* Close on Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') HHPopup.close();
  });

  /* ─────────────────────────────────────────
     3. FAQ ACCORDION
     Used by subscription-faq.liquid.
     Accordion items carry class .sub-faq-item.
     Toggle button carries class .sub-faq-q.
  ───────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    /* Guard: synthetic events (e.g. from chat widgets) can target the
       document itself, which has no .closest() */
    if (!e.target || typeof e.target.closest !== 'function') return;
    var btn = e.target.closest('.sub-faq-q');
    if (!btn) return;

    var item = btn.closest('.sub-faq-item');
    if (!item) return;

    var wasOpen = item.classList.contains('is-open');

    /* Close all */
    item.closest('.sub-card').querySelectorAll('.sub-faq-item').forEach(function (i) {
      i.classList.remove('is-open');
      i.querySelector('.sub-faq-q').setAttribute('aria-expanded', 'false');
    });

    /* Reopen if it was closed */
    if (!wasOpen) {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });

  /* ─────────────────────────────────────────
     4. EMAIL HELPER
     Opens a mailto: link with a pre-populated subject.
     Used by manage-action cards' "Ask us how" buttons.
     Email address falls back to info@happyhounds.co.za.
  ───────────────────────────────────────── */

  window.HHSendEmail = function (subject) {
    var email = (window.HHContactEmail || 'info@happyhounds.co.za');
    window.location.href = 'mailto:' + email + '?subject=' + encodeURIComponent(subject);
  };

})();